// src/pages/Contact.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SparklesIcon, EnvelopeIcon, PhoneIcon, MapPinIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import Footer from "../components/layout/footer.jsx";

function Contact() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (result.success) {
                setIsSubmitted(true);
                setFormData({
                    name: '',
                    email: '',
                    company: '',
                    subject: '',
                    message: ''
                });
            } else {
                alert(result.message || 'Failed to send message. Please try again.');
            }
        } catch (error) {
            console.error('Contact form error:', error);
            alert('Failed to send message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <SEO
                title="Contact PostMgr - Get Support and Sales Information"
                description="Contact PostMgr for support, sales inquiries, partnerships, or general questions about our AI-powered social media management platform."
                url="https://postmgr.com/contact"
            />

            <div className="min-h-screen bg-gray-50">
                {/* Navigation */}
                <nav className="bg-white shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between h-16">
                            <div className="flex items-center">
                                <Link to="/" className="flex items-center">
                                    <img src="/images/Bob_only.svg" alt="PostMgr" className="h-8 w-8" />
                                    <span className="ml-2 text-2xl font-bold text-blue-600">PostMgr</span>
                                </Link>
                            </div>
                            <div className="hidden md:flex items-center space-x-8">
                                <a href="../#features" className="text-gray-700 hover:text-blue-600">Features</a>
                                <a href="../#pricing" className="text-gray-700 hover:text-blue-600">Pricing</a>
                                <Link to="/about" className="text-gray-700 hover:text-blue-600">About</Link>
                                <Link to="/contact" className="text-gray-700 hover:text-blue-600">Contact</Link>
                            </div>
                            <div className="flex items-center space-x-4">
                                <Link to="/login" className="text-gray-700 hover:text-blue-600">
                                    Sign In
                                </Link>
                                <Link
                                    to="/register"
                                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                                >
                                    Get Started
                                </Link>
                            </div>
                        </div>
                    </div>
                </nav>

                {/* Hero Section */}
                <div className="bg-blue-600 text-white py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
                        <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                            Have questions about PostMgr? We're here to help you get the most out of AI-powered social media management.
                        </p>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* Contact Information */}
                        <div className="lg:col-span-1">
                            <h2 className="text-2xl font-bold text-gray-900 mb-8">Get in Touch</h2>

                            <div className="space-y-8">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <EnvelopeIcon className="h-6 w-6 text-blue-600 mt-1" />
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-lg font-medium text-gray-900">Email Us</h3>
                                        <p className="text-gray-600 mt-1">
                                            Get support or ask questions
                                        </p>
                                        <p className="text-blue-600 font-medium mt-2">
                                            <a href="mailto:support@postmgr.com">support@postmgr.com</a>
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <PhoneIcon className="h-6 w-6 text-blue-600 mt-1" />
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-lg font-medium text-gray-900">Call Us</h3>
                                        <p className="text-gray-600 mt-1">
                                            Speak with our sales team
                                        </p>
                                        <p className="text-blue-600 font-medium mt-2">
                                            <a href="tel:+13105702674">+1 (310) 570-2674</a>
                                        </p>
                                        <p className="text-sm text-gray-500 mt-1">
                                            Mon-Fri, 9am-6pm PST
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <MapPinIcon className="h-6 w-6 text-blue-600 mt-1" />
                                    </div>
                                </div>
                            </div>

                            {/* Quick Help */}
                            <div className="mt-12 p-6 bg-blue-50 rounded-lg">
                                <h3 className="text-lg font-semibold text-blue-900 mb-3">Quick Help</h3>
                                <div className="space-y-2">
                                    {/*<Link to="/docs" className="block text-blue-700 hover:text-blue-800">*/}
                                    {/*    📚 Documentation*/}
                                    {/*</Link>*/}
                                    <Link to="/help" className="block text-blue-700 hover:text-blue-800">
                                        ❓ Help Center
                                    </Link>
                                    <Link to="/status" className="block text-blue-700 hover:text-blue-800">
                                        ⚡ Service Status
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-lg shadow-sm p-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>

                                {isSubmitted ? (
                                    <div className="text-center py-12">
                                        <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                                            <span className="text-2xl">✅</span>
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Message Sent!</h3>
                                        <p className="text-gray-600 mb-6">
                                            Thank you for contacting us. We'll get back to you within 24 hours.
                                        </p>
                                        <button
                                            onClick={() => {
                                                setIsSubmitted(false);
                                                setFormData({
                                                    name: '',
                                                    email: '',
                                                    company: '',
                                                    subject: '',
                                                    message: ''
                                                });
                                            }}
                                            className="text-blue-600 hover:text-blue-700 font-medium"
                                        >
                                            Send another message
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Full Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    id="name"
                                                    name="name"
                                                    required
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="John Doe"
                                                />
                                            </div>

                                            <div>
                                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Email Address *
                                                </label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    name="email"
                                                    required
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="john@company.com"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Company
                                                </label>
                                                <input
                                                    type="text"
                                                    id="company"
                                                    name="company"
                                                    value={formData.company}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="Your Company"
                                                />
                                            </div>

                                            <div>
                                                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Subject *
                                                </label>
                                                <select
                                                    id="subject"
                                                    name="subject"
                                                    required
                                                    value={formData.subject}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                >
                                                    <option value="">Select a subject</option>
                                                    <option value="sales">Sales Inquiry</option>
                                                    <option value="support">Technical Support</option>
                                                    <option value="billing">Billing Question</option>
                                                    <option value="partnership">Partnership</option>
                                                    <option value="feedback">Feedback</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                                                Message *
                                            </label>
                                            <textarea
                                                id="message"
                                                name="message"
                                                required
                                                rows={6}
                                                value={formData.message}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="How can we help you?"
                                            />
                                        </div>

                                        <div>
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="w-full bg-blue-600 text-white py-3 px-6 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {isSubmitting ? (
                                                    <span className="flex items-center justify-center">
                                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                        Sending...
                                                    </span>
                                                ) : (
                                                    'Send Message'
                                                )}
                                            </button>
                                        </div>

                                        <p className="text-sm text-gray-500">
                                            * Required fields. We typically respond within 24 hours.
                                        </p>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="bg-white py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
                            <p className="text-lg text-gray-600">
                                Quick answers to common questions
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">How does the AI content generation work?</h3>
                                    <p className="text-gray-600">
                                        Our AI analyzes your brand, audience, and industry trends to generate engaging, relevant content tailored to each social media platform.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I try PostMgr for free?</h3>
                                    <p className="text-gray-600">
                                        Yes! We offer a free plan with 10 posts per month (for a year) and access to 1 social platforms. No credit card required.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Which social media platforms do you support?</h3>
                                    <p className="text-gray-600">
                                        We support LinkedIn, Twitter/X, Bluesky, and more to come. We're constantly adding new platforms based on user demand.
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Is my data secure?</h3>
                                    <p className="text-gray-600">
                                        Absolutely. We use enterprise-grade encryption and security measures to protect your data and social media accounts.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I cancel my subscription anytime?</h3>
                                    <p className="text-gray-600">
                                        Yes, you can cancel your subscription at any time from your account settings. No cancellation fees or long-term commitments.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Do you offer enterprise solutions?</h3>
                                    <p className="text-gray-600">
                                        Yes! Our Enterprise plan includes team collaboration, advanced analytics, API access, and dedicated support.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mt-12">
                            <p className="text-gray-600 mb-4">
                                Don't see your question answered?
                            </p>
                            <Link
                                to="/help"
                                className="inline-flex items-center px-6 py-3 border border-blue-600 text-blue-600 font-medium rounded-md hover:bg-blue-50"
                            >
                                Visit Help Center
                            </Link>
                        </div>
                    </div>
                </div>
                <Footer/>
            </div>
        </>
    );
}

export default Contact;